// qs-store-history.ts
import type { Layer } from '@/types/qs-types-index'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const MAX_HISTORY_ENTRIES = 1000

export const useHistoryStore = defineStore('qs-history', () => {
  const currentDrawingId = ref<string | null>(null)
  const isRecording = ref(true)
  const isRestoring = ref(false)
  const historyEntries = ref<Layer[][]>([])
  const currentIndex = ref(0)

  const hasHistory = computed(() => historyEntries.value.length > 0)
  const customCanUndo = computed(() => currentIndex.value > 0)
  const customCanRedo = computed(() => currentIndex.value < historyEntries.value.length - 1)

  const deepCopyPath = (path: Layer['paths'][0]): Layer['paths'][0] => {
    const copy = { ...path }
    if (path.smoothedPoints && Array.isArray(path.smoothedPoints)) {
      copy.smoothedPoints = [...path.smoothedPoints]
    }
    if (path.visualData) {
      copy.visualData = {
        segments: path.visualData.segments?.map(seg => ({ ...seg, points: [...(seg.points ?? [])] })) ?? [],
        dots: path.visualData.dots?.map(dot => ({ ...dot })) ?? []
      }
    }
    return copy
  }

  const deepCopyLayers = (layers: Layer[]): Layer[] => {
    return layers.map(layer => ({
      ...layer,
      paths: layer.paths ? layer.paths.map(deepCopyPath) : []
    }))
  }

  const initializeForDrawing = (drawingId: string, initialLayers: Layer[] = []) => {
    currentDrawingId.value = drawingId
    isRecording.value = true
    isRestoring.value = false
    historyEntries.value = [deepCopyLayers(initialLayers)]
    currentIndex.value = 0
  }

  const loadHistoryForDrawing = (drawingId: string, layers: Layer[]) => {
    currentDrawingId.value = drawingId
    isRecording.value = true
    isRestoring.value = false
    historyEntries.value = [[...layers]]
    currentIndex.value = 0
  }

  const clearHistoryForDrawing = () => {
    historyEntries.value = []
    currentIndex.value = 0
  }

  const addHistoryEntry = (_action: string, newLayers: Layer[]) => {
    if (!isRecording.value || isRestoring.value || !currentDrawingId.value) {
      return
    }

    if (currentIndex.value < historyEntries.value.length - 1) {
      historyEntries.value = historyEntries.value.slice(0, currentIndex.value + 1)
    }

    historyEntries.value.push(deepCopyLayers(newLayers))
    currentIndex.value = historyEntries.value.length - 1

    if (historyEntries.value.length > MAX_HISTORY_ENTRIES) {
      const removeCount = historyEntries.value.length - MAX_HISTORY_ENTRIES
      historyEntries.value = historyEntries.value.slice(removeCount)
      currentIndex.value = Math.max(0, currentIndex.value - removeCount)
    }
  }

  const performUndo = (): Layer[] | null => {
    if (!customCanUndo.value || isRestoring.value) {
      return null
    }

    isRestoring.value = true
    currentIndex.value--
    const layers = historyEntries.value[currentIndex.value]
    isRestoring.value = false

    return deepCopyLayers(layers)
  }

  const performRedo = (): Layer[] | null => {
    if (!customCanRedo.value || isRestoring.value) {
      return null
    }

    isRestoring.value = true
    currentIndex.value++
    const layers = historyEntries.value[currentIndex.value]
    isRestoring.value = false

    return deepCopyLayers(layers)
  }

  const pauseRecording = () => {
    isRecording.value = false
  }

  const resumeRecording = () => {
    isRecording.value = true
  }

  const getHistoryStats = () => {
    return {
      totalEntries: historyEntries.value.length,
      canUndo: customCanUndo.value,
      canRedo: customCanRedo.value,
      currentIndex: currentIndex.value,
      isRecording: isRecording.value,
      isRestoring: isRestoring.value,
      currentDrawingId: currentDrawingId.value
    }
  }

  const syncWithExternalLayers = (externalLayers: Layer[]) => {
    if (!isRestoring.value && isRecording.value) {
      const currentLayers = historyEntries.value[currentIndex.value] ?? []
      const isDifferent = JSON.stringify(currentLayers) !== JSON.stringify(externalLayers)

      if (isDifferent) {
        const entry = historyEntries.value[currentIndex.value]
        if (entry) {
          historyEntries.value[currentIndex.value] = [...externalLayers]
        }
      }
    }
  }

  return {
    currentDrawingId: computed(() => currentDrawingId.value),
    isRecording: computed(() => isRecording.value),
    isRestoring: computed(() => isRestoring.value),
    layers: computed(() => historyEntries.value[currentIndex.value] ?? []),
    history: computed(() => historyEntries.value),
    hasHistory,
    currentHistoryIndex: computed(() => currentIndex.value),
    canUndo: customCanUndo,
    canRedo: customCanRedo,
    initializeForDrawing,
    loadHistoryForDrawing,
    clearHistoryForDrawing,
    addHistoryEntry,
    performUndo,
    performRedo,
    pauseRecording,
    resumeRecording,
    getHistoryStats,
    syncWithExternalLayers
  }
})